import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { BackupCodeList } from "../auth/SignIn";
import { SESSION_KEY, useSession } from "../auth/session";
import { Card, Field, InlineAlert, PageHeader, PageShell, useToast } from "../company-ui";

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/**
 * Your account (ALN-24; ALN-128 sign-in-methods.md B.5, profile.md): the Sign-in card, where
 * everyone changes their own password or PIN (H21), and the Two-step sign-in card with new backup
 * codes. Each change needs the current secret, and a code when two-step is set up.
 */
export function AccountPage() {
  const { data } = useSession();
  // a username that is an email is the person's email too
  const email = data?.user?.contactEmail ?? (data?.user?.username.includes("@") ? data.user.username : null);
  return (
    <PageShell maxWidth="lg">
      <PageHeader title="Your account" subtitle={`${data?.user?.name ?? ""}${email ? ` · ${email}` : ""}`} />
      <div className="stackgap">
        <SignInCard />
        <p className="muted small m0">Forgot it? An owner or controller can reset it for you in Administration › Users.</p>
        <TwoStepCard />
      </div>
    </PageShell>
  );
}

function SignInCard() {
  const { data } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const pin = data?.accountSignInMethod === "pin";
  const needsCode = Boolean(data?.twoStep?.enrolled);
  const min = data?.pinMinLength ?? 4;
  const close = () => { setOpen(false); setError(null); setTimeout(() => opener.current?.focus(), 0); };
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const code = String(f.get("code") ?? "").replace(/\s/g, "") || undefined;
    setBusy(true); setError(null);
    try {
      if (pin) await api("/me/pin", { body: { currentPin: f.get("current"), pin: f.get("next"), confirm: f.get("confirm"), code } });
      else await api("/me/password", { body: { currentPassword: f.get("current"), password: f.get("next"), confirm: f.get("confirm"), code } });
      toast(`${pin ? "PIN" : "Password"} changed. You're still signed in here; other sessions were signed out.`);
      close();
    } catch (err) { setError(message(err)); }
    setBusy(false);
  };
  const pinInput = (name: string, autoFocus = false) => (
    <input className="input input--touch acct-pin" name={name} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off" autoFocus={autoFocus} />
  );
  return (
    <Card className="acct-card" title="Sign-in" headingId="signin-h" description="How you sign in to Avero Vintner.">
      <div className="card__c stackgap">
        <dl className="acct-dl"><dt>Sign-in name</dt><dd>{data?.user?.username}</dd></dl>
        <div className="acct-row">
          <span>{pin ? "You sign in with a PIN." : "You sign in with a password."}</span>
          {!open && <button ref={opener} className="btn" type="button" onClick={() => setOpen(true)}>{pin ? "Change PIN" : "Change password"}</button>}
        </div>
        {open && (
          <form className="fields fields--narrow" onSubmit={submit} noValidate aria-label={pin ? "Change PIN" : "Change password"}>
            {pin ? (
              <>
                <Field label="Current PIN">{pinInput("current", true)}</Field>
                <Field label="New PIN" hint={`${min} to 6 numbers, not all the same and not in a row.`}>{pinInput("next")}</Field>
                <Field label="Type it again">{pinInput("confirm")}</Field>
              </>
            ) : (
              <>
                <Field label="Current password"><input className="input" name="current" type="password" autoComplete="current-password" autoFocus /></Field>
                <Field label="New password" hint="At least 12 characters. A few words you'll remember work well."><input className="input" name="next" type="password" autoComplete="new-password" /></Field>
                <Field label="Type it again"><input className="input" name="confirm" type="password" autoComplete="new-password" /></Field>
              </>
            )}
            {needsCode && <Field label="6-digit code" hint="From your authenticator app."><input className="input" name="code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} /></Field>}
            {error && <InlineAlert variant="error" title={error} />}
            <div className="field acct-acts">
              <button className="btn btn--primary" type="submit" disabled={busy}>{pin ? "Save PIN" : "Save password"}</button>
              <button className="btn" type="button" onClick={close}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}

function TwoStepCard() {
  const { data } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [panel, setPanel] = useState<"closed" | "code" | "codes">("closed");
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const enrolled = Boolean(data?.twoStep?.enrolled);
  const close = () => { setPanel("closed"); setError(null); setCodes([]); setTimeout(() => opener.current?.focus(), 0); };
  const renew = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = String(new FormData(e.currentTarget).get("code") ?? "").replace(/\s/g, "");
    setBusy(true); setError(null);
    try {
      const r = await api<{ backupCodes: string[] }>("/me/two-step/backup-codes", { body: { code } });
      setCodes(r.backupCodes); setPanel("codes");
    } catch (err) { setError(message(err)); }
    setBusy(false);
  };
  return (
    <Card className="acct-card" title="Two-step sign-in" headingId="twostep-h" description="A 6-digit code from an app on your phone, after your password or PIN.">
      <div className="card__c stackgap">
        {!enrolled ? <p className="m0">Two-step sign-in is off. Your winery hasn't asked you to use it.</p> : (
          <>
            <p className="m0"><b>Two-step sign-in is on.</b> {data?.twoStep?.backupCodesLeft ?? 0} backup {data?.twoStep?.backupCodesLeft === 1 ? "code" : "codes"} left.</p>
            {panel === "closed" && <div><button ref={opener} className="btn" type="button" onClick={() => setPanel("code")}>Make new backup codes</button></div>}
            {panel === "code" && (
              <form className="fields fields--narrow acct-panel" onSubmit={renew} noValidate aria-label="Make new backup codes">
                <p className="m0"><b>Make new backup codes</b></p>
                <p className="m0 muted">Enter a code from your authenticator app. Your old codes stop working once the new ones are made.</p>
                <Field label="6-digit code"><input className="input" name="code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} autoFocus /></Field>
                {error && <InlineAlert variant="error" title={error} />}
                <div className="field acct-acts">
                  <button className="btn btn--primary" type="submit" disabled={busy}>Continue</button>
                  <button className="btn" type="button" onClick={close}>Cancel</button>
                </div>
              </form>
            )}
            {panel === "codes" && (
              <div className="acct-panel">
                <p className="m0"><b>Save your backup codes</b></p>
                <p className="m0 muted">If you don't have your phone, you can sign in with one of these instead of a code. Each one works once. Keep them somewhere safe, away from this computer. Your old codes no longer work.</p>
                <BackupCodeList codes={codes} shared={Boolean(data?.shared)} onSaved={async () => {
                  toast("New backup codes saved. The old ones no longer work.");
                  await qc.invalidateQueries({ queryKey: SESSION_KEY });
                  close();
                }} />
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
